import React, { ReactElement, useRef, useState, WeakValidationMap } from 'react';
import PropTypes from 'prop-types';
import LabelButton from '../LabelButton';
import cx from 'classnames';
import useIsWrapping from '../useIsWrapping';

export type CheckboxButtonOption = {
  /** Optional accessible label applied to the underlying checkbox. By default, the label is used as the aria-label. But if label contains non-text markup such as an icon, this prop is useful for specifying a text-only accessible label. */
  ariaLabel?: string;

  /** Button label to display */
  label: React.ReactNode;

  /** Underlying button value passed to change handler when selected */
  value: string;
};

export interface CheckboxButtonProps {
  /** CSS class to apply to the root div */
  className?: string;

  /** When true, automatically resize the buttons to large when they stack because they can't fit on a single line */
  largeWhenStacked?: boolean;

  /** A button renders for each option. Each `CheckboxButtonOption` has 3 properties: `label: React.ReactNode`, `value: string`, and ariaLabel: string (optional, and applied to the underlying checkbox).  */
  options: Array<CheckboxButtonOption>;

  /** This function is called when a CheckboxButton is clicked. It's passed a standard event handler from the hidden checkbox that's implemented behind the scenes. So check `event.target.checked` to determine if the button was just checked. */
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;

  /** Button size. The large size is automatically applied when the buttons wrap. */
  size?: 'compact' | 'small' | 'medium' | 'large';

  /** Array of selected values. */
  selectedValues: Array<string>;
}

/** CheckboxButton lets users select multiple options from a button group.
 */
export default function CheckboxButton({
  className,
  options,
  largeWhenStacked = true,
  onChange,
  selectedValues,
  size = 'medium',
}: CheckboxButtonProps): ReactElement {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const buttonRef = useRef<HTMLElement[]>([]);
  const stacked = useIsWrapping(buttonRef);

  // If buttons can't fit on one line, set stacked to true
  // so we can set extra styles to make the wrapped buttons the "large" size.
  // Re-run this on window resize.

  return (
    <div
      role="group"
      className={cx(
        'fe_c_checkbox-button',
        `fe_c_checkbox-button--${stacked ? 'stacked' : 'row'}`,
        {
          'fe_c_checkbox-button--large': (stacked && largeWhenStacked) || size === 'large',
        },
        className
      )}
    >
      {options.map((o, index) => {
        const checked = selectedValues.some((v) => v === o.value);
        return (
          <LabelButton
            ref={(el) => {
              if (el) buttonRef.current[index] = el;
            }}
            as="label"
            focused={focusedIndex === index}
            active={checked}
            size={size}
            fullWidth={stacked}
            key={o.value}
          >
            {o.label}
            <input
              className="fe_c_checkbox-button__checkbox"
              aria-label={o.ariaLabel}
              type="checkbox"
              value={o.value}
              onFocus={() => setFocusedIndex(index)}
              onBlur={() => setFocusedIndex(null)}
              onChange={onChange}
              checked={checked}
            />
          </LabelButton>
        );
      })}
    </div>
  );
}

const checkboxButtonPropTypes: WeakValidationMap<CheckboxButtonProps> = {
  /** CSS class to apply to the root div */
  className: PropTypes.string,

  /** When true, automatically resize the buttons to large when they stack because they can't fit on a single line */
  largeWhenStacked: PropTypes.bool,

  /** A button renders for each option. Each `CheckboxButtonOption` has 3 properties:
   * 1. label: `React.ReactNode`,
   * 2. value: `string`
   * 3. aria-label: `string` (optional, and applied to the underlying checkbox).*/
  options: PropTypes.array.isRequired,

  /** This function is called when a `CheckboxButton` is clicked. It's passed a standard event handler from the hidden checkbox that's implemented behind the scenes. So check `event.target.checked` to determine if the button was just checked. */
  onChange: PropTypes.func.isRequired,

  /** Button size. The large size is automatically applied when the buttons wrap. */
  size: PropTypes.oneOf(['compact', 'small', 'medium', 'large']),

  /** Array of selected values. */
  selectedValues: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
};

CheckboxButton.propTypes = checkboxButtonPropTypes;
